import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { getVersionState, VersionState } from 'shared/domain/documentation/versionState';
import {
  isAlreadyLinked,
  isDocumentationLinkType,
  LinkedDocumentationType,
  type LinkedDocumentationItem,
  type LinkedDocumentationTarget,
} from 'shared/domain/issue/linkedDocumentation';

/** What the user links the issue to in the import dialog (PT-7117). */
export interface LinkTargetChoice {
  type: LinkedDocumentationType;
  /** The chosen version, for a link to a version. */
  versionId: string | undefined;
}

/** A version the issue can be linked to, as the dialog lists it. */
export interface LinkableVersion {
  versionId: string;
  number: number | undefined;
  name: string;
  state: VersionState;
  /** The issue links to this version already: the backend refuses duplicates. */
  isLinked: boolean;
}

/** The documentation's versions, newest first, with what the dialog tells about each. */
export function listLinkableVersions(
  documentation: DocumentationModel,
  linkedDocumentation: LinkedDocumentationItem[]
): LinkableVersion[] {
  return [...documentation.versions].reverse().map((version) => ({
    versionId: version._id,
    number: version.number,
    name: version.name,
    state: getVersionState(documentation, version),
    isLinked: isVersionLinked(documentation, version._id, linkedDocumentation),
  }));
}

/** Whether the issue links to the documentation itself already: the backend refuses a second such link. */
export function isDocumentationLinked(
  documentation: DocumentationModel,
  linkedDocumentation: LinkedDocumentationItem[]
): boolean {
  return isAlreadyLinked(linkedDocumentation, {
    type: LinkedDocumentationType.documentation,
    documentationId: documentation._id,
  });
}

function isVersionLinked(
  documentation: DocumentationModel,
  versionId: string,
  linkedDocumentation: LinkedDocumentationItem[]
): boolean {
  return isAlreadyLinked(linkedDocumentation, {
    type: LinkedDocumentationType.version,
    documentationId: documentation._id,
    versionId,
  });
}

/**
 * The dialog's first choice: the documentation itself, with its current version ready to be
 * chosen instead. When the issue links to the documentation already, a version is to be
 * chosen: the current one, unless the issue links to it too.
 */
export function initialLinkTargetChoice(
  documentation: DocumentationModel,
  linkedDocumentation: LinkedDocumentationItem[]
): LinkTargetChoice {
  const currentVersionId = documentation.currentVersionId ?? undefined;
  if (!isDocumentationLinked(documentation, linkedDocumentation)) {
    return { type: LinkedDocumentationType.documentation, versionId: currentVersionId };
  }
  const isCurrentVersionLinkable =
    currentVersionId !== undefined && !isVersionLinked(documentation, currentVersionId, linkedDocumentation);
  return { type: LinkedDocumentationType.version, versionId: isCurrentVersionLinkable ? currentVersionId : undefined };
}

/** The link to make; `undefined` while a version is to be chosen. */
export function toLinkTarget(
  documentation: DocumentationModel,
  choice: LinkTargetChoice
): LinkedDocumentationTarget | undefined {
  if (isDocumentationLinkType(choice.type)) {
    return { type: LinkedDocumentationType.documentation, documentationId: documentation._id };
  }
  return choice.versionId
    ? { type: LinkedDocumentationType.version, documentationId: documentation._id, versionId: choice.versionId }
    : undefined;
}
